import {
    Component,
    computed,
    effect,
    inject,
    input,
    output,
    signal,
    WritableSignal,
} from '@angular/core';
import { Button } from 'primeng/button';
import { Dialog } from 'primeng/dialog';
import { Tag } from 'primeng/tag';
import { Tooltip } from 'primeng/tooltip';
import { AdminGame, GameTemplate, ProductFinalizedArt } from '@/lib-backend';
import {
    ART_GROUP,
    ART_PROP,
    ArtGroup,
    finalizedGroup,
    ProductAssetService,
    readArtProperty,
    readPageUrls,
} from '@/app/services/product-asset.service';
import { ArtOverrides, RealmGamesService } from '@/app/services/realm-games.service';
import { SharedContentService } from '@/app/services/shared-content.service';
import { NotificationService } from '@/app/services/notification.service';
import { AuthService } from '@/app/services/auth.service';
import { AiAssistService } from '@/app/services/ai-assist.service';
import { ImagePreviewService } from '@/app/services/image-preview.service';
import {
    AiFinalizeGameArt,
    FinalizedArtApplied,
} from '@/app/components/ai-finalize-game-art';
import { AiPagesGenerate } from '@/app/components/ai-pages-generate';
import {
    hashPathToken,
    sharedContentPreviewUrl,
} from '@/app/utils/shared-content-url';

const MAX_UPLOAD_BYTES = 5 * 1024 * 1024;

/** One finalized-art variant prepared for a gallery. */
interface VariantView {
    art: ProductFinalizedArt;
    name: string;
    group: ArtGroup;
    iconUrl: string | null;
    bannerUrl: string | null;
    pageUrls: string[];
    iconPreview: string;
    bannerPreview: string;
    pagePreviews: string[];
    applied: boolean;
}

/**
 * Per-realm art for one game — the Art tab of the game editor. Folds the
 * finalized-art picker (two groups: icon/banner pairs and page storyboards) plus
 * upload, AI generation (realm-scoped finalize + pages) and inherit-from-template
 * into one panel. Every action applies immediately to `AdminGameOverrides`;
 * `(gameUpdated)` carries the refreshed game back to the host.
 */
@Component({
    selector: 'app-realm-game-art-panel',
    standalone: true,
    imports: [Button, Dialog, Tag, Tooltip, AiFinalizeGameArt, AiPagesGenerate],
    templateUrl: './realm-game-art-panel.html',
})
export class RealmGameArtPanel {
    readonly adminGame = input<AdminGame | null>(null);
    readonly siteTemplateId = input<string | null>(null);
    readonly active = input<boolean>(true);

    readonly gameUpdated = output<AdminGame>();

    private readonly productAsset = inject(ProductAssetService);
    private readonly gamesService = inject(RealmGamesService);
    private readonly sharedContent = inject(SharedContentService);
    private readonly notification = inject(NotificationService);
    private readonly auth = inject(AuthService);
    private readonly aiAssist = inject(AiAssistService);
    protected readonly preview = inject(ImagePreviewService);

    private readonly variants = signal<ProductFinalizedArt[]>([]);
    protected readonly loading = signal(false);
    /** Id of the variant being applied/deleted (disables its row). */
    protected readonly busyId = signal<string | null>(null);

    protected readonly iconUploading = signal(false);
    protected readonly bannerUploading = signal(false);
    protected readonly imageUploading = signal(false);

    protected readonly aiArtVisible = signal(false);
    protected readonly aiPagesVisible = signal(false);

    protected readonly deleteTarget = signal<VariantView | null>(null);
    protected readonly deleteVisible = signal(false);
    protected readonly deleting = signal(false);

    protected readonly canManage = computed(() => this.auth.isGlobalAdmin());

    protected readonly gameTemplate = computed<GameTemplate | null>(
        () => this.adminGame()?.Template.Template ?? null,
    );

    // ── Effective (override ?? template) art, for the current-state preview ──

    private readonly overrides = computed(() => this.adminGame()?.Overrides);

    /**
     * Realm title override (the name the operator entered for this game), or
     * null when the game inherits the template title. Drives the variant
     * gallery name and the title used for AI generation.
     */
    protected readonly titleOverride = computed(
        () => this.overrides()?.Title ?? null,
    );

    protected readonly effIcon = computed(() => {
        const o = this.overrides();
        return o?.IconUrl ?? this.gameTemplate()?.IconUrl ?? '';
    });
    protected readonly effBanner = computed(() => {
        const o = this.overrides();
        return o?.BannerUrl ?? this.gameTemplate()?.BannerUrl ?? '';
    });
    protected readonly effImages = computed(() => {
        const o = this.overrides();
        return o?.Images ?? this.gameTemplate()?.Images ?? [];
    });
    protected readonly iconOverridden = computed(
        () => this.overrides()?.IconUrl != null,
    );
    protected readonly bannerOverridden = computed(
        () => this.overrides()?.BannerUrl != null,
    );
    protected readonly imagesOverridden = computed(
        () => this.overrides()?.Images != null,
    );

    protected readonly resolve = sharedContentPreviewUrl;

    // ── Variant galleries (two groups, never mixed) ─────────────────────────

    private readonly variantViews = computed<VariantView[]>(() => {
        const o = this.overrides();
        const fallbackName =
            this.titleOverride() ?? this.gameTemplate()?.Title ?? '';
        return this.variants().map((art) => {
            const group = finalizedGroup(art);
            const iconUrl = readArtProperty(art, ART_PROP.Icon);
            const bannerUrl = readArtProperty(art, ART_PROP.Banner);
            const pageUrls = group === ART_GROUP.Pages ? readPageUrls(art) : [];
            const appliedIconBanner =
                !!o &&
                o.IconUrl !== null &&
                o.IconUrl === iconUrl &&
                o.BannerUrl === bannerUrl;
            const appliedPages =
                !!o &&
                o.Images !== null &&
                pageUrls.length > 0 &&
                JSON.stringify(o.Images) === JSON.stringify(pageUrls);
            return {
                art,
                name: readArtProperty(art, ART_PROP.Name) ?? fallbackName,
                group,
                iconUrl,
                bannerUrl,
                pageUrls,
                iconPreview: sharedContentPreviewUrl(iconUrl),
                bannerPreview: sharedContentPreviewUrl(bannerUrl),
                pagePreviews: pageUrls.map((u) => sharedContentPreviewUrl(u)),
                applied:
                    group === ART_GROUP.Pages ? appliedPages : appliedIconBanner,
            };
        });
    });

    protected readonly iconBannerViews = computed(() =>
        this.variantViews().filter((v) => v.group === ART_GROUP.IconBanner),
    );
    protected readonly pagesViews = computed(() =>
        this.variantViews().filter((v) => v.group === ART_GROUP.Pages),
    );

    constructor() {
        effect(() => {
            const active = this.active();
            const site = this.siteTemplateId();
            const tpl = this.gameTemplate();
            if (!active || !site || !tpl) return;
            void this.loadVariants(site, tpl.Id);
            if (this.aiAssist.status() === 'unknown') {
                void this.aiAssist.refreshHealth();
            }
        });
    }

    private async loadVariants(
        siteTemplateId: string,
        gameTemplateId: string,
    ): Promise<void> {
        this.loading.set(true);
        try {
            const items = await this.productAsset.listGameTemplateFinalizedArt(
                siteTemplateId,
                gameTemplateId,
            );
            if (
                this.siteTemplateId() !== siteTemplateId ||
                this.gameTemplate()?.Id !== gameTemplateId
            ) {
                return;
            }
            this.variants.set(items);
        } finally {
            this.loading.set(false);
        }
    }

    private reloadVariants(): void {
        const site = this.siteTemplateId();
        const tpl = this.gameTemplate();
        if (site && tpl) void this.loadVariants(site, tpl.Id);
    }

    // ── Apply / inherit ─────────────────────────────────────────────────────

    private async apply(art: ArtOverrides): Promise<boolean> {
        const g = this.adminGame();
        if (!g) return false;
        const res = await this.gamesService.applyArtOverrides(
            g.RealmId,
            g.Id,
            art,
        );
        if (res.kind === 'ok') {
            this.gameUpdated.emit(res.game);
            return true;
        }
        return false;
    }

    protected async select(view: VariantView): Promise<void> {
        if (this.busyId() || view.applied) return;
        this.busyId.set(view.art.Id);
        try {
            await this.apply(
                view.group === ART_GROUP.Pages
                    ? { images: view.pageUrls }
                    : {
                          iconUrl: view.iconUrl ?? undefined,
                          bannerUrl: view.bannerUrl ?? undefined,
                      },
            );
        } finally {
            this.busyId.set(null);
        }
    }

    protected async inheritIconBanner(): Promise<void> {
        await this.apply({ iconUrl: null, bannerUrl: null });
    }

    protected async inheritImages(): Promise<void> {
        await this.apply({ images: null });
    }

    // ── Delete a variant ────────────────────────────────────────────────────

    protected askDelete(view: VariantView): void {
        if (this.busyId()) return;
        this.deleteTarget.set(view);
        this.deleteVisible.set(true);
    }

    protected async confirmDelete(): Promise<void> {
        const view = this.deleteTarget();
        if (!view || this.deleting()) return;
        this.deleting.set(true);
        try {
            const ok = await this.productAsset.deleteFinalizedArt(view.art.Id);
            if (ok) {
                this.variants.update((list) =>
                    list.filter((a) => a.Id !== view.art.Id),
                );
                this.notification.show({
                    severity: 'success',
                    summary: 'Variant deleted',
                });
                this.deleteVisible.set(false);
                this.deleteTarget.set(null);
            }
        } finally {
            this.deleting.set(false);
        }
    }

    // ── Upload (direct override) ─────────────────────────────────────────────

    protected async uploadIcon(target: EventTarget | null): Promise<void> {
        const url = await this.uploadOne(this.iconUploading, target);
        if (url) await this.apply({ iconUrl: url });
    }

    protected async uploadBanner(target: EventTarget | null): Promise<void> {
        const url = await this.uploadOne(this.bannerUploading, target);
        if (url) await this.apply({ bannerUrl: url });
    }

    protected async uploadImage(target: EventTarget | null): Promise<void> {
        const url = await this.uploadOne(this.imageUploading, target);
        if (url) await this.apply({ images: [...this.effImages(), url] });
    }

    protected async removeImage(url: string): Promise<void> {
        await this.apply({ images: this.effImages().filter((u) => u !== url) });
    }

    private async uploadOne(
        flag: WritableSignal<boolean>,
        target: EventTarget | null,
    ): Promise<string | null> {
        const input = target as HTMLInputElement;
        const file = input.files?.[0];
        input.value = '';
        const site = this.siteTemplateId();
        const tpl = this.gameTemplate();
        if (!file || !site || !tpl) return null;
        if (file.size > MAX_UPLOAD_BYTES) {
            const limitMb = (MAX_UPLOAD_BYTES / (1024 * 1024)).toFixed(1);
            this.notification.show({
                severity: 'warn',
                summary: 'File too large',
                detail: `"${file.name}" exceeds the ${limitMb} MB limit.`,
            });
            return null;
        }
        flag.set(true);
        try {
            const hashed = await hashPathToken(site);
            const path = `products/${tpl.Id}/${hashed}/${crypto.randomUUID()}`;
            return await this.sharedContent.uploadImageFile(path, file);
        } finally {
            flag.set(false);
        }
    }

    // ── AI generation (realm-scoped) ─────────────────────────────────────────

    protected openAiArt(): void {
        if (this.canManage() && this.adminGame()) this.aiArtVisible.set(true);
    }

    protected openAiPages(): void {
        if (this.canManage() && this.adminGame()) this.aiPagesVisible.set(true);
    }

    protected async onAiArtApplied(applied: FinalizedArtApplied): Promise<void> {
        await this.apply({
            iconUrl: applied.iconUrl,
            bannerUrl: applied.bannerUrl,
        });
        this.reloadVariants();
    }

    protected async onAiPagesApplied(urls: string[]): Promise<void> {
        await this.apply({ images: urls });
        this.reloadVariants();
    }
}
